import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import type { Bundle, CarePlan } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import {
  getCarePlanEpisodeId,
  getCarePlanQuestionnaireResponseId,
  isTreatmentPlanCarePlan,
} from '@unify/fhir-model/resources/treatment-plan-careplan';

export interface TreatmentPlanVersionRow {
  key: string;
  carePlanId: string;
  versionId?: string;
  title: string;
  status: string;
  updatedAt?: string;
  /** Chart-document QuestionnaireResponse id linked from this CarePlan (same across versions). */
  chartDocumentId?: string;
  isCurrent: boolean;
}

function versionRecency(plan: CarePlan): string {
  return plan.meta?.lastUpdated ?? plan.created ?? '';
}

export function treatmentPlanVersionRows(
  plans: CarePlan[],
  histories: Array<{ carePlanId: string; versions: CarePlan[] }>,
): TreatmentPlanVersionRow[] {
  const historyById = new Map(histories.map((entry) => [entry.carePlanId, entry.versions]));
  const rows: TreatmentPlanVersionRow[] = [];
  for (const plan of plans) {
    if (!plan.id) continue;
    const versions = historyById.get(plan.id);
    const list = versions && versions.length > 0 ? versions : [plan];
    const currentVersionId = plan.meta?.versionId;
    for (const version of list) {
      const versionId = version.meta?.versionId;
      const isCurrent = versionId != null && currentVersionId != null
        ? versionId === currentVersionId
        : version === plan || version === list[0];
      rows.push({
        key: `${plan.id}:${versionId ?? 'current'}`,
        carePlanId: plan.id,
        versionId,
        title: version.title || 'Diagnosis and Treatment Plan',
        status: version.status ?? 'unknown',
        updatedAt: versionRecency(version) || undefined,
        chartDocumentId:
          getCarePlanQuestionnaireResponseId(version)
          ?? getCarePlanQuestionnaireResponseId(plan),
        isCurrent,
      });
    }
  }
  return rows.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
}

export function useEpisodeTreatmentPlanVersions(
  patientId: string | undefined,
  episodeId: string | undefined,
): { rows: TreatmentPlanVersionRow[]; loading: boolean } {
  const medplum = useMedplum();
  const [rows, setRows] = useState<TreatmentPlanVersionRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patientId || !episodeId) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const plans = await medplum.searchResources('CarePlan', {
          subject: `Patient/${patientId}`,
          _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
          _count: '100',
        });
        const episodePlans = plans.filter(
          (plan) => isTreatmentPlanCarePlan(plan) && getCarePlanEpisodeId(plan) === episodeId && plan.id,
        );
        const histories = await Promise.all(
          episodePlans.map(async (plan) => {
            try {
              const history = await medplum.readHistory('CarePlan', plan.id!) as Bundle<CarePlan>;
              const versions = (history.entry ?? [])
                .map((entry) => entry.resource)
                .filter((resource): resource is CarePlan => resource?.resourceType === 'CarePlan');
              return { carePlanId: plan.id!, versions };
            } catch {
              return { carePlanId: plan.id!, versions: [plan] };
            }
          }),
        );
        if (!cancelled) setRows(treatmentPlanVersionRows(episodePlans, histories));
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [episodeId, medplum, patientId]);

  return { rows, loading };
}
